import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Box, useTheme } from '@ublend-npm/aulaui-core';
import { getHomeViewRole } from '@app/selectors/home';
import { WidgetCard } from '../../common';
import QuickLinkTile from './QuickLinkTile';
import useHomeQuickLinks from '../useHomeQuickLinks';
import { toggleWidgetPreference } from '../../../../../../actions/home/widgetPreferences';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isQuickLinksInstitutionLevelEnabled } from '@core/constants/launchDarklyFeatureFlags';
import { Skeleton } from '@ublend-npm/aulaui-core';
import useTrackHomeWidgetLoaded from '../../common/useTrackHomeWidgetLoaded';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';

const QuickLinks: React.FC = () => {
  const currentRole = useSelector(getHomeViewRole);
  const dispatch = useDispatch();
  const flags = useFlags();
  const isQuickLinkEnabled = isQuickLinksInstitutionLevelEnabled(flags);
  const roleHrefKey = currentRole === 'student' ? 'learner' : 'educator';
  const { quickLinks, isLoading, hasError } = useHomeQuickLinks(currentRole);
  const visibleLinks = quickLinks.filter((link) => link.href[roleHrefKey]);
  const theme = useTheme();

  const titleLeading = (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      width={40}
      height={40}
      mr={1}
      flexShrink={0}
      sx={{
        borderRadius: '100px',
        bgcolor: theme.palette.mode === 'dark' ? '#000000' : '#EAE8ED',
        color: 'primary.main',
      }}
    >
      <LinkOutlinedIcon sx={{ fontSize: 24 }} />
    </Box>
  );
  useTrackHomeWidgetLoaded(
    'quick_links',
    !isLoading && !hasError,
    visibleLinks.length
  );

  const handleHide = () => {
    dispatch(toggleWidgetPreference('home-quick-links-widget', true) as any);
  };

  const menuActions = [{ id: 'hide', label: 'Hide', onClick: handleHide }];

  const scrollRef = useRef<HTMLDivElement>(null);
  const [activePage, setActivePage] = useState(0);

  const columns = 2;
  const rows = 4;
  const itemsPerPage = columns * rows;

  const pages: typeof visibleLinks[] = [];
  for (let i = 0; i < visibleLinks.length; i += itemsPerPage) {
    pages.push(visibleLinks.slice(i, i + itemsPerPage));
  }

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el || pages.length === 0) return;
    const pageWidth = el.clientWidth;
    const index = Math.round(el.scrollLeft / pageWidth);
    setActivePage(Math.min(Math.max(index, 0), pages.length - 1));
  };

  const scrollToPage = (index: number) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: el.clientWidth * index, behavior: 'smooth' });
  };

  useEffect(() => {
    setActivePage(0);
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [visibleLinks.length]);

  if (!isQuickLinkEnabled) {
    return null;
  }

  if (isLoading) {
    return (
      <WidgetCard
        title="Quick links"
        menuActions={menuActions}
        titleLeading={titleLeading}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '16px',
          }}
        >
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton
              key={index}
              data-testid="quick-links-skeleton"
              kind="rounded"
              sx={{
                height: '100px',
                bgcolor: theme.palette.background.default,
                borderRadius: '16px',
              }}
            />
          ))}
        </Box>
      </WidgetCard>
    );
  }

  if (hasError || visibleLinks.length === 0) {
    return null;
  }
  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        ref={scrollRef}
        onScroll={handleScroll}
        sx={{
          display: 'flex',
          gap: '32px',
          alignItems: 'flex-start',
          overflowX: 'auto',
          overflowY: 'visible',
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        {pages.map((pageLinks, pageIndex) => (
          <Box
            key={pageIndex}
            sx={{
              flex: '0 0 100%',
              scrollSnapAlign: 'start',
              display: 'grid',
              gridTemplateColumns: `repeat(${columns}, 1fr)`,
              alignContent: 'start',
              columnGap: '16px',
              rowGap: '16px',
            }}
          >
            {pageLinks.map((link) => (
              <QuickLinkTile key={link.key} link={link} role={currentRole} />
            ))}
          </Box>
        ))}
      </Box>

      {pages.length > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '8px',
            mt: '16px',
          }}
        >
          {pages.map((_, index) => (
            <Box
              key={index}
              component="button"
              onClick={() => scrollToPage(index)}
              aria-label={`Go to page ${index + 1}`}
              sx={{
                width: index === activePage ? '10px' : '8px',
                height: index === activePage ? '10px' : '8px',
                borderRadius: '50%',
                border: 'none',
                padding: 0,
                cursor: 'pointer',
                backgroundColor:
                  index === activePage ? 'rgba(0,0,0,0.7)' : 'rgba(0,0,0,0.25)',
                transition: 'all 0.2s ease',
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <WidgetCard
        title="Quick links"
        titleLeading={titleLeading}
        menuActions={menuActions}
      >
        {content}
      </WidgetCard>
    </>
  );
};

export default QuickLinks;
